import { mixpanelService } from './mixpanel';
import { supabase } from '../lib/supabase';

interface ActivityConfig {
  heartbeatInterval: number; // milliseconds
  activityThreshold: number; // milliseconds - how long until considered inactive
  throttleDelay: number; // milliseconds - minimum time between activity detections
}

interface SessionData {
  sessionId: string;
  userId: string | null;
  startTime: Date;
  lastActivity: Date;
  isActive: boolean;
  isPaused: boolean;
  pauseReason?: string;
}

export class ActivityTracker {
  private config: ActivityConfig = {
    heartbeatInterval: 30000, // 30 seconds
    activityThreshold: 120000, // 2 minutes (more forgiving for reading)
    throttleDelay: 1000, // 1 second
  };

  private session: SessionData | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private activityThrottle: NodeJS.Timeout | null = null;
  private lastActivityDetection: number = 0;

  // Activity detection listeners
  private activityListeners: Array<{
    element: EventTarget;
    event: string;
    handler: EventListener;
  }> = [];

  constructor() {
    this.handleActivity = this.handleActivity.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.heartbeatTick = this.heartbeatTick.bind(this);
  }

  /**
   * Get current user ID from Supabase session
   */
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id || null;
    } catch (error) {
      console.error('[ActivityTracker] Failed to get user ID:', error);
      return null;
    }
  }

  /**
   * Start tracking user activity
   */
  async startTracking(): Promise<void> {
    if (this.session) {
      console.log('[ActivityTracker] Already tracking');
      return;
    }

    // Get current user ID once
    const userId = await this.getCurrentUserId();
    
    // Create new session
    this.session = {
      sessionId: this.generateSessionId(),
      userId: userId,
      startTime: new Date(),
      lastActivity: new Date(),
      isActive: true,
      isPaused: false,
    };

    // Set up activity listeners
    this.setupActivityListeners();

    // Start heartbeat timer
    this.startHeartbeat();

    // Track session start
    mixpanelService.track('session_started', {
      session_id: this.session.sessionId,
      user_id: this.session.userId,
      timestamp: this.session.startTime.toISOString(),
    });

    console.log('[ActivityTracker] Started tracking session:', this.session.sessionId);
  }

  /**
   * Stop tracking and end session
   */
  stopTracking(): void {
    if (!this.session) {
      console.log('[ActivityTracker] No session to stop');
      return;
    }

    const sessionId = this.session.sessionId;
    const totalActiveMinutes = this.calculateActiveMinutes();

    // Track session end
    mixpanelService.track('session_ended', {
      session_id: sessionId,
      user_id: this.session.userId,
      total_active_minutes: totalActiveMinutes,
      session_duration_minutes: Math.floor(
        (Date.now() - this.session.startTime.getTime()) / 60000
      ),
      timestamp: new Date().toISOString(),
    });

    this.cleanup();
    console.log('[ActivityTracker] Session ended:', sessionId, 'Active minutes:', totalActiveMinutes);
  }

  /**
   * Pause tracking (when tab becomes hidden or user goes idle)
   */
  pauseSession(reason: string): void {
    if (!this.session || this.session.isPaused) {
      console.log('[ActivityTracker] Cannot pause - no session or already paused');
      return;
    }

    const sessionId = this.session.sessionId;
    this.session.isPaused = true;
    this.session.isActive = false;
    this.session.pauseReason = reason;

    const activeMinutes = this.calculateActiveMinutes();

    mixpanelService.track('session_paused', {
      session_id: sessionId,
      user_id: this.session.userId,
      pause_reason: reason,
      active_minutes: activeMinutes,
      timestamp: new Date().toISOString(),
    });

    this.stopHeartbeat();
    console.log('[ActivityTracker] Session paused:', reason);
  }

  /**
   * Resume tracking (when tab becomes visible or user becomes active)
   */
  resumeSession(): void {
    if (!this.session || !this.session.isPaused) {
      console.log('[ActivityTracker] Cannot resume - no session or not paused');
      return;
    }

    const sessionId = this.session.sessionId;
    this.session.isPaused = false;
    this.session.isActive = true;
    this.session.lastActivity = new Date();
    this.session.pauseReason = undefined;

    mixpanelService.track('session_resumed', {
      session_id: sessionId,
      user_id: this.session.userId,
      timestamp: new Date().toISOString(),
    });

    this.startHeartbeat();
    console.log('[ActivityTracker] Session resumed');
  }

  /**
   * Set up event listeners for activity detection
   */
  private setupActivityListeners(): void {
    if (typeof window === 'undefined') return;

    const events = [
      { element: document, event: 'mousemove' },
      { element: document, event: 'mousedown' },
      { element: document, event: 'mouseup' },
      { element: document, event: 'click' },
      { element: document, event: 'scroll' },
      { element: document, event: 'keydown' },
      { element: document, event: 'keyup' },
      { element: window, event: 'focus' },
      { element: document, event: 'visibilitychange' },
      // Add more "reading" indicators
      { element: document, event: 'touchstart' },
      { element: document, event: 'touchmove' },
      { element: window, event: 'resize' },
    ];

    events.forEach(({ element, event }) => {
      const handler = event === 'visibilitychange' ? this.handleVisibilityChange : this.handleActivity;
      element.addEventListener(event, handler, { passive: true });
      this.activityListeners.push({ element, event, handler });
    });
  }

  /**
   * Handle detected user activity (throttled)
   */
  private handleActivity(): void {
    const now = Date.now();

    // Throttle activity detection
    if (now - this.lastActivityDetection < this.config.throttleDelay) {
      return;
    }

    this.lastActivityDetection = now;

    if (!this.session) {
      // Auto-start tracking on first activity
      this.startTracking();
      return;
    }

    // Update last activity time
    this.session.lastActivity = new Date();

    // Resume session if it was paused due to inactivity
    if (this.session.isPaused) {
      this.resumeSession();
    } else {
      // Mark as active if it wasn't already
      this.session.isActive = true;
    }
  }

  /**
   * Handle visibility changes (tab hidden/visible)
   */
  private handleVisibilityChange(): void {
    if (!this.session) return;

    if (document.hidden) {
      // Only pause after tab has been hidden for 30 seconds
      // This prevents brief tab switches from pausing the session
      setTimeout(() => {
        if (document.hidden && this.session && !this.session.isPaused) {
          this.pauseSession('tab_hidden_extended');
        }
      }, 30000); // 30 second delay
    } else {
      // Auto-resume when tab becomes visible again (but don't override inactivity pause)
      if (this.session && this.session.isPaused) {
        // Only auto-resume if the pause reason was tab visibility, not inactivity
        if (this.session.pauseReason?.includes('tab_hidden')) {
          this.resumeSession();
        }
      }
    }
  }

  /**
   * Start the heartbeat timer
   */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;

    this.heartbeatTimer = setInterval(this.heartbeatTick, this.config.heartbeatInterval);
  }

  /**
   * Stop the heartbeat timer
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Heartbeat tick - check activity and send heartbeat event
   */
  private heartbeatTick(): void {
    if (!this.session || this.session.isPaused) return;

    const now = new Date();
    const secondsSinceLastActivity = Math.floor(
      (now.getTime() - this.session.lastActivity.getTime()) / 1000
    );

    // Check if user has been inactive too long
    if (secondsSinceLastActivity > this.config.activityThreshold / 1000) {
      this.session.isActive = false;
      this.pauseSession('inactivity');
      return;
    }

    // Send heartbeat event
    mixpanelService.track('activity_heartbeat', {
      session_id: this.session.sessionId,
      user_id: this.session.userId,
      is_active: this.session.isActive,
      seconds_since_last_interaction: secondsSinceLastActivity,
      current_page: window.location.pathname,
      timestamp: now.toISOString(),
    });
  }

  /**
   * Calculate active minutes for current session
   */
  private calculateActiveMinutes(): number {
    if (!this.session) return 0;

    const sessionDuration = Date.now() - this.session.startTime.getTime();
    return Math.floor(sessionDuration / 60000);
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clean up all resources
   */
  private cleanup(): void {
    // Remove event listeners
    this.activityListeners.forEach(({ element, event, handler }) => {
      try {
        element.removeEventListener(event, handler);
      } catch (error) {
        console.warn('[ActivityTracker] Error removing event listener:', error);
      }
    });
    this.activityListeners = [];

    // Clear timers
    this.stopHeartbeat();
    if (this.activityThrottle) {
      clearTimeout(this.activityThrottle);
      this.activityThrottle = null;
    }

    // Reset session
    this.session = null;
  }

  /**
   * Get current session info
   */
  getSessionInfo(): SessionData | null {
    return this.session;
  }

  /**
   * Force activity detection (for manual triggers like search/click)
   */
  recordActivity(activityType?: string): void {
    this.handleActivity();

    // Optional: track specific activity types
    if (activityType && this.session) {
      mixpanelService.track('manual_activity', {
        session_id: this.session.sessionId,
        user_id: this.session.userId,
        activity_type: activityType,
        timestamp: new Date().toISOString(),
      });
    }
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker();